import { Model, modelRegistry } from './Model';
import { PriceCalculation, PriceLevelingItem } from '.';
import { PriceLevelingType } from '../types';
import Printable from '../print/Printable';
import { documentHeader, documentSignatures, documentSpacer, documentTable, factLine } from '../print/DocumentLayout';
import Money from '../utilities/Money';
import { I18n } from '../wrappers/I18n';
import Store from '../wrappers/Store';
import moment from 'moment-timezone';
export class PriceLeveling extends Model {
    static entity = 'price_levelings';
    static TYPE_MANUAL = PriceLevelingType.manual;
    static TYPE_AUTO = PriceLevelingType.auto;
    id = null;
    user_id = null;
    price_calculation_id = null;
    number = null;
    type = null;
    date = null;
    description = null;
    recorded_at = null;
    created_at = null;
    updated_at = null;
    items = [];
    priceCalculation;
    static fields() {
        return {
            id: this.attr(null),
            user_id: this.attr(null),
            price_calculation_id: this.attr(null),
            number: this.attr(null),
            type: this.attr(null),
            date: this.attr(null),
            description: this.attr(null),
            recorded_at: this.attr(null),
            created_at: this.attr(null),
            updated_at: this.attr(null),
            items: this.attr([], (value) => (value || []).map(item => new PriceLevelingItem(item))),
            priceCalculation: this.attr(undefined, value => value ? new PriceCalculation(value) : undefined),
        };
    }
    constructor(record) {
        super();
        this.$fill(record);
    }
    getOldSellingValue() {
        return this.items.reduce((acc, cur) => acc.add(cur.getOldSellingValue()), Money.parse(0));
    }
    getOldTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getOldTax()), Money.parse(0));
    }
    getOldSellingValueAfterTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getOldSellingValueAfterTax()), Money.parse(0));
    }
    getSellingValue() {
        return this.items.reduce((acc, cur) => acc.add(cur.getSellingValue()), Money.parse(0));
    }
    getTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0));
    }
    getSellingValueAfterTax() {
        return this.items.reduce((acc, cur) => acc.add(cur.getSellingValueAfterTax()), Money.parse(0));
    }
    getPriceDifference() {
        return this.items.reduce((acc, cur) => acc.add(cur.getPriceDifference()), Money.parse(0));
    }
    getPrintable() {
        const pr = new Printable();
        const venue = Store.get('tenant')?.venue;
        if (!venue)
            return pr;
        documentHeader(pr, {
            title: I18n.t('price_levelings.heading'),
            facts: factLine([I18n.t('price_levelings.number'), this.number], [I18n.t('price_levelings.date'), moment(this.date).format('DD.MM.YYYY.')], [I18n.t('price_levelings.description'), this.description]),
            venueName: venue.name,
            venueLine: `${I18n.t('general.tax_id_number')}: ${venue.tax_id_number}`,
        });
        documentSpacer(pr);
        const figure = (key) => ({ label: I18n.t(`price_levelings.${key}`), kind: 'figure' });
        const columns = [
            { label: I18n.t('general.number'), kind: 'index' },
            { label: I18n.t('general.code'), kind: 'code' },
            { label: I18n.t('general.inventory_item'), kind: 'name' },
            figure('quantity'),
            figure('old_selling_price'),
            figure('old_selling_price_after_tax'),
            figure('selling_price'),
            figure('selling_price_after_tax'),
            figure('old_selling_value'),
            figure('old_tax'),
            figure('old_selling_value_after_tax'),
            figure('selling_value'),
            { label: `${I18n.t('price_levelings.tax_rate')} %`, kind: 'figure' },
            figure('tax'),
            figure('selling_value_after_tax'),
            figure('price_difference'),
        ];
        const rows = this.items.map((item, index) => [
            String(index + 1),
            item.getProduct()?.code || '',
            item.getProduct()?.name || '',
            `${item.getQuantity()} ${item.getUnitOfMeasure()}`,
            item.getOldSellingPrice().toFormat('0.00'),
            item.getOldSellingPriceAfterTax().toFormat('0.00'),
            item.getSellingPrice().toFormat('0.00'),
            item.getSellingPriceAfterTax().toFormat('0.00'),
            item.getOldSellingValue().toFormat('0.00'),
            item.getOldTax().toFormat('0.00'),
            item.getOldSellingValueAfterTax().toFormat('0.00'),
            item.getSellingValue().toFormat('0.00'),
            item.getTaxRate() + '',
            item.getTax().toFormat('0.00'),
            item.getSellingValueAfterTax().toFormat('0.00'),
            item.getPriceDifference().toFormat('0.00'),
        ]);
        documentTable(pr, columns, rows, {
            density: 'dense',
            totals: {
                label: I18n.t('general.total').toUpperCase(),
                span: 8,
                values: [
                    this.getOldSellingValue().toFormat('0.00'),
                    this.getOldTax().toFormat('0.00'),
                    this.getOldSellingValueAfterTax().toFormat('0.00'),
                    this.getSellingValue().toFormat('0.00'),
                    '',
                    this.getTax().toFormat('0.00'),
                    this.getSellingValueAfterTax().toFormat('0.00'),
                    this.getPriceDifference().toFormat('0.00'),
                ],
            },
        });
        documentSignatures(pr, [I18n.t('general.created_by'), I18n.t('general.received_by'), I18n.t('general.responsible_person')]);
        return pr;
    }
}
export default PriceLeveling;
modelRegistry.register('PriceLeveling', PriceLeveling);
//# sourceMappingURL=PriceLeveling.js.map